/**
 * Web Push do lado do cliente. Liga a permissão do browser + o `PushManager` à
 * subscrição guardada no servidor. Tudo aqui é defensivo: o push é um extra —
 * um browser sem suporte, sem permissão ou sem VAPID configurado no servidor
 * simplesmente não activa, e a app corre igual.
 *
 * A subscrição é do DISPOSITIVO (não da conta): vive no `PushManager` deste
 * browser e é sincronizada com o servidor por `endpoint`. Sair da sessão não a
 * apaga de propósito — voltar a entrar no mesmo dispositivo reaproveita-a.
 */
import { api } from "./api";

export type PushState = "unsupported" | "disabled" | "denied" | "enabled";

/** Só faz sentido mexer no push se o browser tiver as três peças. */
export function pushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Estado actual para a UI desenhar o toggle. Não pede permissões. */
export async function getPushState(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? "enabled" : "disabled";
}

/**
 * Pede permissão (se ainda não foi decidida), subscreve no `PushManager` com a
 * chave pública do servidor e regista a subscrição na API. Idempotente: se já
 * houver subscrição, só a re-sincroniza com o servidor.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";

  const { enabled, publicKey } = await api.push.vapid();
  if (!enabled || !publicKey) return "disabled";

  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "disabled";

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  await sendToServer(sub);
  return "enabled";
}

/** Desliga o push neste dispositivo: cancela no browser E remove no servidor. */
export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => false);
    await api.push.unsubscribe(endpoint).catch(() => {});
  }
  return "disabled";
}

/**
 * Re-sincroniza no arranque: se a permissão já foi dada, garante que o servidor
 * conhece a subscrição actual (o endpoint pode ter rodado, ou a linha ter sido
 * podada por um envio falhado). Silencioso — nunca pede permissão.
 */
export async function syncPush(): Promise<void> {
  if (!pushSupported() || Notification.permission !== "granted") return;
  try {
    const { enabled } = await api.push.vapid();
    if (!enabled) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sendToServer(sub);
  } catch {
    // Sincronização é best-effort; um falhanço aqui não afecta o resto da app.
  }
}

async function sendToServer(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!json.endpoint || !p256dh || !auth) return;
  await api.push.subscribe({ endpoint: json.endpoint, keys: { p256dh, auth } });
}

/**
 * A chave VAPID vem em base64url; o `PushManager` quer bytes. O tipo é
 * `Uint8Array<ArrayBuffer>` (e não o genérico `Uint8Array<ArrayBufferLike>`) de
 * propósito: `applicationServerKey` exige um `BufferSource` sobre `ArrayBuffer`,
 * por isso o buffer é alocado explicitamente em vez de deixar o TS inferir.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
